import { Box } from '@mui/system';
import { useContext, useState } from 'react';
import { DateRangePicker } from 'react-date-range';
import { CustomButton } from '../elements/button';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { GET_RECONCILIATION_REPORT } from '../graphql/query';
import { AppContext } from '../context/app-context';
import { useQueryHook } from '../hooks/use-query.hook';
import { Autocomplete, Grid, TextField } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { CustomSelect, MenuItemType } from '../elements/select';
import {
  reportFilterConfig,
  reportTimezoneTypeConfig,
} from '../constants/constants';
import {
  ReportFilterType,
  ReportTimezoneType,
  ReportTypeConfig,
} from '../types';
import { extractAllErrorMessages } from '../utils/graphql';
import { useSnackbar } from 'notistack';
import { Text } from '../elements/text';
import moment from 'moment';
import TimezoneSelect, {
  ITimezoneOption,
  allTimezones,
} from 'react-timezone-select';

export const ReconciliationPage = () => {
  const current = new Date();
  current.setHours(0);
  current.setMinutes(0);
  current.setSeconds(0);

  const appContext = useContext(AppContext);
  const [dateRange, setDateRange] = useState<any>([
    {
      startDate: current,
      endDate: current,
      key: 'selection',
    },
  ]);
  const [isRequested, setIsRequested] = useState<boolean>(false);
  const [reportFilterType, setReportFilterType] = useState<string>(
    ReportFilterType.ADMIN,
  );
  const [reportTimezoneFilterType, setReportTimezoneFilterType] =
    useState<string>(ReportTimezoneType.USER_LOCAL);
  const [reportFilterValueIDs, setReportFilterValueIDs] = useState<string[]>(
    [],
  );
  const [selectedTimezone, setSelectedTimezone] = useState<ITimezoneOption>({
    value: '',
    label: '',
  });
  const { enqueueSnackbar } = useSnackbar();

  const { fetch: getReport, loading } = useQueryHook({
    query: GET_RECONCILIATION_REPORT,
    skipInitialCall: true,
    onCompleted: () => {
      console.log(appContext?.adminID?.value);
      setIsRequested(true);
      enqueueSnackbar(
        `${reportFilterConfig[reportFilterType].label} report successful.`,
        {
          variant: 'success',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
        },
      );
    },
    onError: (err) => {
      enqueueSnackbar(extractAllErrorMessages(err), {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      });
    },
  });

  const reportTypeSelectData = Object.keys(reportFilterConfig).map(
    (filterType) => {
      const menuItem: MenuItemType = {
        value: filterType,
        label: reportFilterConfig[filterType].label,
      };
      return menuItem;
    },
  );

  const reportTimezoneSelectData = reportTimezoneTypeConfig.map(
    (timezoneType: ReportTypeConfig) => {
      const timezone: MenuItemType = {
        value: timezoneType.name,
        label: timezoneType.label,
      };
      return timezone;
    },
  );

  const getFilterValuesByType = (): any[] => {
    const filterType = reportFilterType;
    const { filterValueQuery, filterValueQueryName } =
      reportFilterConfig[filterType];
    const { data } = useQueryHook({ query: filterValueQuery });
    // console.log({ data })
    return data?.[filterValueQueryName] || [];
  };

  const getSelectedFilterValues = (): any[] => {
    const reportFilterValues = getFilterValuesByType();
    if (reportFilterType === ReportFilterType.ADMIN) {
      return reportFilterValues.filter((reportFilterValue) =>
        reportFilterValueIDs.includes(reportFilterValue.id),
      );
    } else {
      return reportFilterValues.filter((reportFilterValue) =>
        reportFilterValueIDs.includes(reportFilterValue._id),
      );
    }
  };

  const getAdminReport = () => {
    setIsRequested(false);
    // Remove timezone info while sending dates to make it timezone agnostic
    const startDate = moment(dateRange[0]?.startDate).format('YYYY-MM-DD');
    const endDate = moment(dateRange[0]?.endDate).format('YYYY-MM-DD');
    getReport({
      reportType: reportFilterType,
      adminId: appContext?.adminID?.value,
      filters: {
        startDate,
        endDate,
        ...(ReportFilterType.ADMIN === reportFilterType
          ? {
              venueFilter: {
                ids: reportFilterValueIDs,
              },
            }
          : {}),
        ...(ReportFilterType.VENDOR === reportFilterType
          ? {
              vendorFilter: {
                ids: reportFilterValueIDs,
              },
            }
          : {}),
        ...(ReportFilterType.RUNNER === reportFilterType
          ? {
              runnerFilter: {
                ids: reportFilterValueIDs,
              },
            }
          : {}),
        ...(ReportFilterType.RUNNER_EARNINGS === reportFilterType
          ? {
              runnerFilter: {
                ids: reportFilterValueIDs,
              },
            }
          : {}),
        ...(ReportFilterType.VENDOR_ORDER_STATUS === reportFilterType
          ? {
              vendorOrderStatusFilter: {
                ids: reportFilterValueIDs,
              },
            }
          : {}),
      },
      timezoneType: reportTimezoneFilterType,
      customTimezone: selectedTimezone.value,
    });
  };

  return (
    <>
      <Grid container xs={'auto'} justifyContent={'center'} flexWrap={'wrap'}>
        <Grid item xs={'auto'} marginTop={4}>
          <DateRangePicker
            months={1}
            ranges={dateRange}
            direction="horizontal"
            rangeColors={[]}
            maxDate={current}
            onChange={(item) => {
              setDateRange([item.selection]);
            }}
          />
        </Grid>
        <Grid item xs={4} marginTop={4} marginLeft={4}>
          <CustomSelect
            required
            data={reportTypeSelectData}
            onChange={(value: string) => {
              setReportFilterType(value);
              setIsRequested(false);
            }}
            title="Report Type"
            value={reportFilterType}
          />
          <Box marginTop={1}>
            <Text text={'Select Filter'} />
            <Autocomplete
              multiple={true}
              id="tags-standard"
              options={getFilterValuesByType()}
              // still useful for rendering selected values
              getOptionLabel={(option: any) => {
                const { showOptionsBy } = reportFilterConfig[reportFilterType];
                return option[showOptionsBy] || '';
              }}
              // isOptionEqualToValue={(option, value) => option._id === value._id}
              value={getSelectedFilterValues()}
              onChange={(e, values) => {
                setReportFilterValueIDs(
                  values.map((value) =>
                    reportFilterType === ReportFilterType.ADMIN
                      ? value.id
                      : value._id,
                  ),
                );
                setIsRequested(false);
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 35, // controls overall input area height
                  padding: '2px 8px', // reduce internal padding
                },
                '& .MuiInputBase-input': {
                  padding: '4px 6px', // reduce input element padding
                },
                '& .MuiChip-root': {
                  height: 22, // smaller chips for multiple mode
                  fontSize: 12,
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  helperText={'Optional'}
                />
              )}
              renderOption={(props, option) => {
                const { showOptionsBy } = reportFilterConfig[reportFilterType];
                return (
                  <li {...props} key={option._id}>
                    {option[showOptionsBy]}
                  </li>
                );
              }}
            />
          </Box>
          <CustomSelect
            required
            data={reportTimezoneSelectData}
            onChange={(value: string) => {
              setReportTimezoneFilterType(value);
              setIsRequested(false);
            }}
            title="Timezone Type"
            value={reportTimezoneFilterType}
          />
          {reportTimezoneFilterType === ReportTimezoneType.CUSTOM && (
            <Box
              marginTop={1}
              sx={{
                position: 'relative',
                '& .react-timezone-select__control .react-select__indicators, & .react-timezone-select__control .react-timezone-select__indicator, & .react-select__indicator, & .react-select__dropdown-indicator, & .react-timezone-select__control svg, & .react-timezone-select__control .react-select__indicators > *, & [class*="indicator"], & [class*="Indicator"], & [class*="dropdown-indicator"]':
                  {
                    display: 'none !important',
                  },
                '& .react-timezone-select, & .react-timezone-select__control, & .react-timezone-select__input':
                  {
                    minHeight: 36,
                    height: 36,
                    fontSize: 13,
                  },
              }}
            >
              <Text text={'Timezone'} />
              <TimezoneSelect
                value={selectedTimezone}
                title="Venue Timezone"
                onChange={setSelectedTimezone}
                selectProps={{
                  components: {
                    DropdownIndicator: () => null,
                    IndicatorSeparator: () => null,
                  },
                }}
                style={{ minHeight: 36, height: 36 }}
                timezones={{
                  ...allTimezones,
                  'America/Lima': 'Pittsburgh',
                  'Europe/Berlin': 'Frankfurt',
                }}
              />
              <ArrowDropDownIcon
                sx={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  fontSize: 20,
                }}
              />
            </Box>
          )}
        </Grid>
        <Grid item xs={8}>
          <Box m={4}>
            <CustomButton
              title="Get Data"
              onClick={getAdminReport}
              // disabled={isRequested}
              loading={loading}
              loadingText={'Please wait...'}
            />
          </Box>
          <Box width={'100%'} display={'flex'} justifyContent={'center'}>
            {isRequested && (
              <Text
                text={`${reportFilterConfig[reportFilterType].label} report will be sent to you via email shortly.`}
                fontSize={16}
                fontWeight={600}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
